/**
 * useMicrophone — wraps MediaRecorder + AudioContext analyser so the
 * recorder UI can stay declarative.
 *
 * State machine:
 *   "idle"       → before any user action
 *   "permission" → requesting getUserMedia (might prompt OS dialog)
 *   "recording"  → MediaRecorder is collecting chunks, analyser feeds
 *                  live FFT samples to the caller via getFftSnapshot()
 *   "stopped"    → MediaRecorder stopped, blob+samples available
 *   "denied"     → user rejected mic permission (terminal until reload)
 *   "error"      → unexpected failure (codec mismatch, etc.)
 *
 * Browser support:
 *   • MediaRecorder MIME: prefer audio/webm;codecs=opus (Chrome, FF, Edge);
 *     fall back to audio/mp4 (Safari iOS 14.5+); if neither, "error".
 *   • iOS Safari requires user gesture to start AudioContext — we call
 *     resume() defensively before each start().
 */
import { useCallback, useEffect, useRef, useState } from "react";

export type MicrophoneStatus =
  | "idle"
  | "permission"
  | "recording"
  | "stopped"
  | "denied"
  | "error";

const PREFERRED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

/** Pick the first MediaRecorder-supported MIME from the preference list. */
function pickMimeType(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  for (const mime of PREFERRED_MIME_TYPES) {
    try {
      if (MediaRecorder.isTypeSupported(mime)) return mime;
    } catch {
      // silent: some browsers throw on unknown MIME
    }
  }
  return null;
}

export type MicrophoneHandle = {
  status: MicrophoneStatus;
  errorMessage: string | null;
  /** Recording time in seconds (updates every animation frame). */
  elapsedSec: number;
  /** Last recorded blob — null until status === "stopped". */
  blob: Blob | null;
  /** MIME type used for the blob (e.g. "audio/webm;codecs=opus"). */
  mimeType: string | null;
  /**
   * Read the current FFT magnitudes (256 bins, 0..1). Caller fills a
   * pre-allocated Uint8Array to avoid per-frame allocation. Returns
   * `length` to signal how many bins were written; 0 when no analyser.
   */
  getFftSnapshot: (target: Uint8Array) => number;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  cancel: () => void;
};

export function useMicrophone(): MicrophoneHandle {
  const [status, setStatus] = useState<MicrophoneStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [mimeType, setMimeType] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);

  const cleanup = useCallback(() => {
    if (tickRef.current !== null) {
      cancelAnimationFrame(tickRef.current);
      tickRef.current = null;
    }
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try {
        recorderRef.current.stop();
      } catch {
        // silent: stopping an already-inactive recorder throws on some browsers
      }
    }
    recorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => {
        try {
          t.stop();
        } catch {
          // silent: track already stopped
        }
      });
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {
        // silent: closing twice throws
      });
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const start = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setStatus("error");
      setErrorMessage("getUserMedia_unsupported");
      return;
    }
    const mime = pickMimeType();
    if (!mime) {
      setStatus("error");
      setErrorMessage("media_recorder_unsupported");
      return;
    }

    setStatus("permission");
    setErrorMessage(null);
    setElapsedSec(0);
    setBlob(null);
    chunksRef.current = [];

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
    } catch (err) {
      const name = (err as DOMException)?.name ?? "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setStatus("denied");
      } else {
        setStatus("error");
        setErrorMessage(name || "getUserMedia_failed");
      }
      return;
    }

    streamRef.current = stream;
    const AudioCtor = window.AudioContext || (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtor) {
      cleanup();
      setStatus("error");
      setErrorMessage("audio_context_unsupported");
      return;
    }
    const ctx = new AudioCtor();
    if (ctx.state === "suspended") {
      try {
        await ctx.resume();
      } catch {
        // silent: iOS sometimes needs a second resume(); we keep going
      }
    }
    audioCtxRef.current = ctx;
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512; // 256 bins, sufficient for voice waveform
    analyser.smoothingTimeConstant = 0.55;
    source.connect(analyser);
    analyserRef.current = analyser;

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType: mime });
    } catch (err) {
      cleanup();
      setStatus("error");
      setErrorMessage((err as Error).message || "media_recorder_init_failed");
      return;
    }
    setMimeType(mime);
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const finalBlob = new Blob(chunksRef.current, { type: mime });
      setBlob(finalBlob);
      setStatus("stopped");
    };
    recorder.onerror = (e) => {
      setStatus("error");
      setErrorMessage((e as ErrorEvent)?.message || "recorder_error");
    };

    recorderRef.current = recorder;
    startedAtRef.current = performance.now();
    recorder.start(100); // chunk every 100ms — enables smooth size estimation if needed
    setStatus("recording");

    const tick = () => {
      const seconds = (performance.now() - startedAtRef.current) / 1000;
      setElapsedSec(seconds);
      tickRef.current = requestAnimationFrame(tick);
    };
    tickRef.current = requestAnimationFrame(tick);
  }, [cleanup]);

  const stop = useCallback(async () => {
    if (tickRef.current !== null) {
      cancelAnimationFrame(tickRef.current);
      tickRef.current = null;
    }
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      // Recorder already stopped (e.g. by browser permission revoke) —
      // we still want to release the stream + AudioContext.
      cleanup();
      return;
    }
    return new Promise<void>((resolve) => {
      const originalOnStop = recorder.onstop;
      recorder.onstop = (ev) => {
        originalOnStop?.call(recorder, ev);
        // Drop stream + AudioContext now that the blob is in state.
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => {
            try {
              t.stop();
            } catch {
              // silent
            }
          });
          streamRef.current = null;
        }
        if (audioCtxRef.current) {
          audioCtxRef.current.close().catch(() => {
            // silent
          });
          audioCtxRef.current = null;
        }
        analyserRef.current = null;
        resolve();
      };
      try {
        recorder.stop();
      } catch {
        resolve();
      }
    });
  }, [cleanup]);

  const cancel = useCallback(() => {
    cleanup();
    setStatus("idle");
    setElapsedSec(0);
    setBlob(null);
    chunksRef.current = [];
  }, [cleanup]);

  const getFftSnapshot = useCallback((target: Uint8Array): number => {
    const analyser = analyserRef.current;
    if (!analyser) return 0;
    const len = Math.min(target.length, analyser.frequencyBinCount);
    // TS 5.7 narrowed Uint8Array's backing-buffer generic to ArrayBuffer
    // (excluding SharedArrayBuffer). The DOM lib types getByteFrequencyData
    // with Uint8Array<ArrayBuffer>; callers passing a default Uint8Array
    // (ArrayBufferLike) hit a structural mismatch. Cast at the boundary —
    // the analyser only reads into the buffer, so the runtime contract
    // is identical.
    analyser.getByteFrequencyData(target as Uint8Array<ArrayBuffer>);
    return len;
  }, []);

  return {
    status,
    errorMessage,
    elapsedSec,
    blob,
    mimeType,
    getFftSnapshot,
    start,
    stop,
    cancel,
  };
}
